import { useAuth, useClerk } from "@clerk/clerk-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ApolloProvider } from "@/app/_/config/ApolloProvider";
import { State } from "@/state";
import type { StyleThemeType } from "@/state/@types";
import { Scope } from "./Scope";

/**
 * Re-mint the token this long before it lapses, and refuse to mount behind one
 * with less than this left on it. A request that leaves with a spent token is
 * answered "Authentication required" - sent again with a fresh one
 * (``ApolloProvider``).
 */
const TOKEN_REFRESH_MARGIN_MS = 60_000;

/**
 * How long a stored Clerk token has left, in milliseconds; 0 when there is
 * none, or when its payload will not decode.
 *
 * Exported so tests exercise the same reading the gate below makes.
 */
export function tokenMillisRemaining(token: string | null): number {
  if (!token) return 0;
  const payload = token.split(".")[1];
  if (!payload) return 0;
  try {
    const { exp } = JSON.parse(atob(payload)) as { exp?: number };
    if (typeof exp !== "number") return 0;
    return Math.max(exp * 1000 - Date.now(), 0);
  } catch {
    return 0;
  }
}

/**
 * The token a request leaves with (``AuthSync``): the one stored while it has
 * time left, else one minted for it. *mint* mints one whatever is stored;
 * *rejected* - the server turned the stored one down - asks Clerk past its
 * own cache. None to be had, the person is signed out and sent to sign in.
 */
export type FreshToken = (options?: {
  mint?: boolean;
  rejected?: boolean;
}) => Promise<string | null>;

/**
 * Keeps the Clerk token every request, mutation and socket handshake reads in
 * localStorage, and renders the app's Apollo client - handed the way to a fresh
 * one - once there is one to read.
 */
function AuthSync({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // A token's *presence* is not a credential. A 1-day token left here
  // overnight used to wave the app through: it mounted, fired its first wave
  // of queries against the expired one, and every view came back empty.
  const [isTokenReady, setIsTokenReady] = useState(() => {
    try {
      return (
        tokenMillisRemaining(localStorage.getItem("jwtToken")) >
        TOKEN_REFRESH_MARGIN_MS
      );
    } catch {
      return false;
    }
  });
  // Clerk's latest, for a token minted outside a render - by a request, the
  // refresh timer, or the tab shown again.
  const clerk = useRef({
    getToken,
    isSignedIn,
    signOut,
    navigate,
    pathname,
    search,
  });
  clerk.current = { getToken, isSignedIn, signOut, navigate, pathname, search };

  const [freshToken] = useState<FreshToken>(() => {
    let minting: Promise<string | null> | null = null;
    let refresh: ReturnType<typeof setTimeout> | undefined;
    const freshToken: FreshToken = ({
      mint = false,
      rejected = false,
    } = {}) => {
      const stored = localStorage.getItem("jwtToken");
      if (
        !mint &&
        !rejected &&
        tokenMillisRemaining(stored) > TOKEN_REFRESH_MARGIN_MS
      ) {
        return Promise.resolve(stored);
      }
      // One at a time: every request that finds it spent waits on the one.
      minting ??= (async () => {
        // Never inside the render or layout pass that asked: the router takes
        // no navigation there.
        await Promise.resolve();
        const { getToken, isSignedIn, signOut, navigate, pathname, search } =
          clerk.current;
        clearTimeout(refresh);
        let jwtToken: string | null = null;
        if (isSignedIn) {
          try {
            jwtToken = await getToken({
              template: "1-day",
              skipCache: rejected,
            });
          } catch {
            jwtToken = null;
          }
        }
        if (!jwtToken) {
          localStorage.removeItem("jwtToken");
          if (isSignedIn) {
            // Clerk session exists but credentials aren't active — force a
            // clean sign-out.
            localStorage.removeItem("global-store");
            await signOut();
          }
          // Signed out on a page of the app, it has nothing to show: to sign
          // in, and back to that page once they have - a link to an
          // invitation, say. A public page is theirs to read signed out.
          if (isSignedIn || pathname.startsWith("/app")) {
            navigate(
              pathname.startsWith("/app")
                ? `/auth/sign-in?${new URLSearchParams({ redirect_url: `${pathname}${search}` })}`
                : "/auth/sign-in",
              { replace: true },
            );
          }
          return null;
        }
        localStorage.setItem("jwtToken", jwtToken);
        // Minted again before it lapses - never sooner than half a minute
        // away, so a token we cannot read the expiry of can't spin this into
        // a loop. A sleeping laptop or a tab in the background runs no timer:
        // the tab shown again, and each request, check it too.
        refresh = setTimeout(
          () => void freshToken({ mint: true }),
          Math.max(
            tokenMillisRemaining(jwtToken) - TOKEN_REFRESH_MARGIN_MS,
            30_000,
          ),
        );
        return jwtToken;
      })().finally(() => {
        minting = null;
      });
      return minting;
    };
    return freshToken;
  });

  useLayoutEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    void freshToken({ mint: true }).then(() => {
      if (!cancelled) setIsTokenReady(true);
    });
    // Back after the timer could not run: a token spent meanwhile is minted
    // before anything asks with it.
    const onShown = () => {
      if (document.visibilityState === "visible") void freshToken();
    };
    document.addEventListener("visibilitychange", onShown);
    window.addEventListener("focus", onShown);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onShown);
      window.removeEventListener("focus", onShown);
    };
  }, [freshToken, isSignedIn, isLoaded]);

  // Don't render children (including ApolloProvider) until token is synced
  if (!isTokenReady) {
    return null;
  }

  return <ApolloProvider freshToken={freshToken}>{children}</ApolloProvider>;
}

/**
 * The app's frame: the corners chosen (``data-style-theme``, which the
 * theme's variables follow), the token kept, the scope. *styleTheme* holds a
 * page to one style whatever was chosen - a shared report is drawn rounded.
 */
export function ThemeRegistry({
  styleTheme: styleThemeProp,
  children,
}: {
  styleTheme?: StyleThemeType;
  children: React.ReactNode;
}) {
  const chosen = State((state) => state.styleTheme);
  const styleTheme = styleThemeProp ?? chosen;
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-style-theme", styleTheme);
  }, [styleTheme]);

  return (
    <AuthSync>
      <Scope>{children}</Scope>
    </AuthSync>
  );
}
